/**
 * Collapse overlapping calls to the same async function into one.
 *
 * Written for the token refresh, where a duplicate call is not merely wasteful. The
 * refresh token rotates and the previous one is blacklisted, so a second concurrent
 * refresh presents a cookie the first has already invalidated: it gets a 401, clears
 * the access token, and signs the person out mid-session. A dashboard that fires four
 * queries at once produces that race every time the access token expires.
 *
 * The flight is cleared as soon as it settles, success or failure. Nothing is cached:
 * the next caller gets a real call. Latching a failure would strand a signed-in person
 * on a dropped connection, and latching a success would hand out a stale token.
 */
export function singleFlight<T>(operation: () => Promise<T>): () => Promise<T> {
  let inFlight: Promise<T> | null = null;

  return (): Promise<T> => {
    if (inFlight !== null) {
      return inFlight;
    }
    const flight = operation().finally(() => {
      // Only clear our own flight. A `finally` that ran after a later call had already
      // started would otherwise wipe that one's promise and let a third caller open a
      // second concurrent request -- the exact thing this guard exists to prevent.
      if (inFlight === flight) {
        inFlight = null;
      }
    });
    inFlight = flight;
    return flight;
  };
}
